import { TFunction } from 'i18next';
import _get from 'lodash/get';
import { SsoIntegration, SsoMappingField } from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import {
  SsoConfigTab,
  SsoIntegrationFieldNames,
  DEFAULT_TIME_TOLERANCE_SECONDS,
} from './constants';
import getSsoPresetLabel from './getSsoPresetLabel';
import { MappingRow } from './presetDefaults';

export const DEFAULT_VALUES: Values = {
  [SsoIntegrationFieldNames.ConnectionDetailsHeader]: undefined,
  [SsoIntegrationFieldNames.ProviderPreset]: null,
  [SsoIntegrationFieldNames.Name]: '',
  [SsoIntegrationFieldNames.IconAttached]: null,
  [SsoIntegrationFieldNames.CompanyDomainIds]: [],
  [SsoIntegrationFieldNames.CompanyDomainIdsList]: undefined,
  [SsoIntegrationFieldNames.SsoConfigurationHeader]: undefined,
  [SsoIntegrationFieldNames.Code]: '',
  [SsoIntegrationFieldNames.RegenerateCodeButton]: undefined,
  [SsoIntegrationFieldNames.IdpMetadata]: '',
  [SsoIntegrationFieldNames.ReadFromXmlButton]: undefined,
  [SsoIntegrationFieldNames.ConfigTab]: SsoConfigTab.Attributes,
  [SsoIntegrationFieldNames.AttributeMapping]: [
    { attribute: '', mapsTo: null },
  ],
  [SsoIntegrationFieldNames.JitProvisioningEnabled]: false,
  [SsoIntegrationFieldNames.TimeToleranceSeconds]:
    DEFAULT_TIME_TOLERANCE_SECONDS,
  [SsoIntegrationFieldNames.SyncUserAttributes]: false,
  [SsoIntegrationFieldNames.AllowIdpInitiated]: false,
  [SsoIntegrationFieldNames.SpConfiguration]: undefined,
};

export default function getDefaultValuesFromQueryData(
  data: SsoIntegration | undefined,
  t: TFunction,
  supportedMappingFields: SsoMappingField[] = [],
): Values {
  if (!data?.id) {
    return DEFAULT_VALUES;
  }

  const companyDomains = _get(data, 'companyDomains', []);
  const attributeMapping = data.attributeMapping as Record<
    string,
    string
  > | null;

  // Convert attributeMapping JSON object to array format for the form
  let mappingRows: MappingRow[] = [{ attribute: '', mapsTo: null }];

  if (attributeMapping && typeof attributeMapping === 'object') {
    const fieldOrder = new Map(
      supportedMappingFields.map((f) => [f.key, f.sortIndex]),
    );
    mappingRows = Object.entries(attributeMapping)
      .map(([attribute, mapsTo]) => ({
        attribute,
        mapsTo: mapsTo ? { value: mapsTo, label: mapsTo } : null,
      }))
      .sort((a, b) => {
        const ai =
          a.mapsTo?.value !== undefined
            ? fieldOrder.get(a.mapsTo.value) ?? Infinity
            : Infinity;
        const bi =
          b.mapsTo?.value !== undefined
            ? fieldOrder.get(b.mapsTo.value) ?? Infinity
            : Infinity;
        return ai - bi;
      });
  }

  return {
    ...DEFAULT_VALUES,
    [SsoIntegrationFieldNames.ProviderPreset]: {
      value: data.providerPreset,
      label: getSsoPresetLabel(data.providerPreset, t),
    },
    [SsoIntegrationFieldNames.Name]: data.name ?? '',
    [SsoIntegrationFieldNames.IconAttached]: data.icon || null,
    [SsoIntegrationFieldNames.CompanyDomainIds]: companyDomains.map(
      (domain: { id: string; domain: string }) => ({
        value: domain.id,
        label: domain.domain,
      }),
    ),
    [SsoIntegrationFieldNames.Code]: data.code ?? '',
    [SsoIntegrationFieldNames.IdpMetadata]: data.idpMetadata ?? '',
    [SsoIntegrationFieldNames.AttributeMapping]: mappingRows,
    [SsoIntegrationFieldNames.JitProvisioningEnabled]:
      data.jitProvisioningEnabled ?? false,
    [SsoIntegrationFieldNames.TimeToleranceSeconds]:
      data.timeToleranceSeconds ?? DEFAULT_TIME_TOLERANCE_SECONDS,
    [SsoIntegrationFieldNames.SyncUserAttributes]: _get(
      data,
      'syncUserAttributes',
      false,
    ),
    [SsoIntegrationFieldNames.AllowIdpInitiated]:
      data.allowIdpInitiated ?? false,
  };
}
